import React from 'react';
// Leaflet CSS ko import karna zaroori hai warna map sahi nahi dikhega
import 'leaflet/dist/leaflet.css';
import {
  Building2,
  AlertTriangle,
  FileText,
  TrendingUp,
  Download,
  MoreVertical,
  MapIcon,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { useSelector } from 'react-redux';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { RxDashboard } from 'react-icons/rx';

// --- MOCK DATA (FILTERED FOR CLUBS, HOTELS, RESTAURANTS) ---
const incidentData = [
  { month: 'Sep', incidents: 12 },
  { month: 'Oct', incidents: 18 },
  { month: 'Nov', incidents: 10 },
  { month: 'Dec', incidents: 45 }, // Higher in Dec due to holiday season in hotels/clubs
  { month: 'Jan', incidents: 32 },
  { month: 'Feb', incidents: 20 },
];

const enforcementData = [
  { id: 1, name: 'Club Neon Pulse', violation: 'Overcrowding (Capacity > 200%)', action: 'Seal Order', date: '2026-02-23', status: 'Critical' },
  { id: 2, name: 'Grand Imperial Hotel', violation: 'Kitchen Fire Suppression Fail', action: 'Legal Notice', date: '2026-02-22', status: 'Warning' },
  { id: 3, name: 'The Spicy Spoon Restaurant', violation: 'Hygiene Standards', action: 'Fine Imposed', date: '2026-02-21', status: 'Resolved' },
  { id: 4, name: 'Azure Beach Club', violation: 'Blocked Fire Exits', action: 'Warning Issued', date: '2026-02-20', status: 'Critical' },
  { id: 5, name: 'Hotel Royal Orchid', violation: 'Liquor License Expired', action: 'Notice Served', date: '2026-02-19', status: 'Warning' },
];

const complianceData = [
  { name: 'Compliant', value: 88 },
  { name: 'Non-Compliant', value: 12 },
];
const COLORS = ['#10b981', '#e2e8f0'];

// Updated Zones to reflect Hospitality/Nightlife clusters
const highRiskZones = [
  { id: 1, pos: [15.5494, 73.7535], name: "Zone A: Beach Club Belt", risk: "Critical" },
  { id: 2, pos: [15.4909, 73.8278], name: "Zone B: City Hotel District", risk: "High" },
  { id: 3, pos: [15.5762, 73.7444], name: "Zone C: Nightlife Strip (Baga)", risk: "Medium" },
  { id: 4, pos: [15.5174, 73.8036], name: "Zone D: Riverfront Restaurants", risk: "High" },
];

// --- HELPER COMPONENTS ---
const Card = ({ children, className = "" }) => (
  <div className={`bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 ${className}`}>
    {children}
  </div>
);

const Badge = ({ status }) => {
  const styles = {
    Critical: "bg-red-50 text-red-700 border-red-200",
    Warning: "bg-amber-50 text-amber-700 border-amber-200",
    Resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Info: "bg-blue-50 text-blue-700 border-blue-200"
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.Info}`}>
      {status}
    </span>
  );
};

// --- MAIN CONTENT COMPONENT ---
const HODDashboardContent = () => {
  const themeMode = useSelector((state) => state.theme?.mode);
  const textColorClass = themeMode ? "dark_text-color" : "text-primary";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
  const borderThem = themeMode ? "border_them_2" : "border-primary";

  return (
    <div className={`${bgColorClass} min-h-screen`}>
      {/* HEADER */}
      <div className={`py-4 md:px-12 px-6 border-b flex justify-between items-center h-[60px] bg-white sticky top-0 z-50 ${borderThem}`}>

        <div
          className={`flex ps-3 md:ps-0 ${textColorClass} text-sm md:text-base items-center gap-x-2`}
        >
          <RxDashboard size={16} />
          <p className="font-semibold">HOD Dashboard (Hospitality)</p>
        </div>
        <div className="hidden md:block text-sm text-slate-500 bg-slate-50 px-3 py-1 rounded-full border border-slate-200 shadow-sm">
          Last updated: Today, 09:42 AM
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12 py-6 overflow-y-auto h-[calc(91vh-0px)] pt-4 pb-10 space-y-6">

        {/* 1. KPI CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Card className="p-5">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-sm font-medium text-slate-500">Establishments (risk level)</p>
                <h3 className="text-2xl font-bold text-slate-900 mt-1">1,248</h3>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600"><Building2 size={20} /></div>
            </div>
            <div className="space-y-2">
              <div className="flex h-2 w-full rounded-full overflow-hidden bg-slate-100">
                <div className="w-[60%] bg-emerald-500" title="Hotels"></div>
                <div className="w-[30%] bg-amber-400" title="Restaurants"></div>
                <div className="w-[10%] bg-red-500" title="Clubs"></div>
              </div>
              <div className="flex justify-between text-xs text-slate-400">
                <span>Hotels: 60%</span>
                <span>Rest: 30%</span>
                <span>Clubs: 10%</span>
              </div>
            </div>
          </Card>

          <Card className="p-5 border-l-4 border-l-red-500">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-red-600">Critical Alerts Today</p>
                <div className="flex items-center gap-2 mt-1">
                  <h3 className="text-3xl font-bold text-slate-900">07</h3>
                  <span className="flex h-3 w-3 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                </div>
              </div>
              <div className="p-2 bg-red-100 rounded-lg text-red-600 animate-pulse">
                <AlertTriangle size={20} />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4">Mostly Overcrowding & Fire Safety.</p>
          </Card>

          <Card className="p-5">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="text-sm font-medium text-slate-500">Expiring Approvals</p>
                <h3 className="text-2xl font-bold text-amber-600 mt-1">42</h3>
              </div>
              <div className="p-2 bg-amber-50 rounded-lg text-amber-600"><FileText size={20} /></div>
            </div>
            <p className="text-xs font-medium text-slate-500 bg-amber-50 inline-block px-2 py-1 rounded">
              Next 30 Days
            </p>
          </Card>

          <Card className="p-5">
            <div className="flex justify-between items-start">
              <p className="text-sm font-medium text-slate-500">Compliance</p>
              <span className="text-xs font-bold text-emerald-600 flex items-center">
                <TrendingUp size={14} className="mr-1" /> +2.4%
              </span>
            </div>
            <div className="flex items-center gap-4 mt-2 h-16">
              <div className="w-16 h-16 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={complianceData}
                      innerRadius={20}
                      outerRadius={30}
                      startAngle={180}
                      endAngle={0}
                      dataKey="value"
                      stroke="none"
                    >
                      {complianceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute bottom-0 left-0 w-full text-center text-xs font-bold text-slate-700">88%</div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-emerald-600">Good Standing</h4>
                <p className="text-xs text-slate-400">Target: 90%</p>
              </div>
            </div>
          </Card>
        </div>

        {/* 2. MAP & TREND CHART */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="h-[450px] overflow-hidden flex flex-col relative z-0">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <MapIcon size={18} className="text-blue-500" />
                High-Risk Hospitality Zones
              </h3>
              <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-md font-medium">Critical Areas</span>
            </div>
            <div className="flex-1 z-0">
              <MapContainer center={[15.5494, 73.7535]} zoom={11} style={{ height: "100%", width: "100%" }}>   <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {highRiskZones.map((zone) => (
                  <Marker key={zone.id} position={zone.pos}>
                    <Popup>
                      <div className="p-1">
                        <h4 className="font-bold text-red-600">{zone.name}</h4>
                        <p className="text-xs">Risk: {zone.risk}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </Card>

          <Card className="h-[450px] flex flex-col">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <TrendingUp size={18} className="text-slate-500" />
                Monthly Incidents (Clubs/Hotels)
              </h3>
            </div>
            <div className="flex-1 p-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={incidentData}>
                  <defs>
                    <linearGradient id="colorIncidents" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="3 3" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="incidents" stroke="#3b82f6" strokeWidth={3} fill="url(#colorIncidents)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* 3. TABLE */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
            <h3 className="font-semibold text-slate-800">Enforcement Actions (Hospitality)</h3>
            <button className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-md text-xs font-medium hover:bg-slate-50">
              <Download size={14} /> Report
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr>
                  <th className="px-6 py-4">Establishment</th>
                  <th className="px-6 py-4">Violation</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {enforcementData.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-slate-900">{row.name}</td>
                    <td className="px-6 py-4 text-slate-600">{row.violation}</td>
                    <td className="px-6 py-4"><Badge status={row.status} /></td>
                    <td className="px-6 py-4 text-right">
                      <button className="text-slate-400 hover:text-slate-600"><MoreVertical size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default HODDashboardContent;